import { AxiosInstance, AxiosRequestConfig } from "axios"
import {
  api,
  authenticatedApi,
  handleApiResponse,
  handleApiError,
} from "./client"

type HttpMethod = "get" | "post" | "put" | "patch" | "delete"

export interface ResourceOptions {
  requiresAuth?: boolean
  resourceName: string
}

export const createResource = <T>(
  endpoint: string,
  options: ResourceOptions,
) => {
  const { requiresAuth = true, resourceName } = options
  const client: AxiosInstance = requiresAuth ? authenticatedApi : api

  return {
    get: async (params?: any, config?: AxiosRequestConfig): Promise<T> => {
      try {
        const response = await client.get(endpoint, { params, ...config })
        return handleApiResponse<T>(response)
      } catch (error) {
        return handleApiError(error, resourceName)
      }
    },

    getById: async (id: string, config?: AxiosRequestConfig): Promise<T> => {
      try {
        const response = await client.get(`${endpoint}/${id}`, config)
        return handleApiResponse<T>(response)
      } catch (error) {
        return handleApiError(error, resourceName)
      }
    },

    post: async (data: any, config?: AxiosRequestConfig): Promise<T> => {
      try {
        const response = await client.post(endpoint, data, config)
        return handleApiResponse<T>(response)
      } catch (error) {
        return handleApiError(error, resourceName)
      }
    },

    patch: async (
      id: string,
      data: any,
      config?: AxiosRequestConfig,
    ): Promise<T> => {
      try {
        const response = await client.patch(`${endpoint}/${id}`, data, config)
        return handleApiResponse<T>(response)
      } catch (error) {
        return handleApiError(error, resourceName)
      }
    },

    delete: async (id: string, config?: AxiosRequestConfig): Promise<T> => {
      try {
        const response = await client.delete(`${endpoint}/${id}`, config)
        return handleApiResponse<T>(response)
      } catch (error) {
        return handleApiError(error, resourceName)
      }
    },

    // Custom request method for more complex scenarios
    request: async (
      method: HttpMethod,
      url: string,
      data?: any,
      config?: AxiosRequestConfig,
    ): Promise<T> => {
      try {
        const response = await client.request({
          method,
          url: `${endpoint}${url}`,
          data,
          ...config,
        })
        return handleApiResponse<T>(response)
      } catch (error) {
        return handleApiError(error, resourceName)
      }
    },
  }
}
